import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import SettingsFormContainer from 'src/Libs/form/components/SettingsFormContainer'
import { NumberField } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import useGridAndSystemProtectionMutation from '../hooks/use-grid-and-system-protection-mutation'
import useSyncFormWithValues from '../hooks/use-sync-form-with-values'
import { FUNCTION_NUMBER } from '../types/common'
import { MainVoltageLimitsSettingsModel } from '../types/grid-and-system-protection.model'
import { getMainVoltageLimitsSettingsSchema, MainVoltageLimitsSettingsForm } from '../types/schemas'
import { getChangedFieldStyle, isFieldDisabled } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
const NAME_SPACE = 'systemSettings.grid-and-system-protection-configuration.main-voltage-limits'

type Props = {
  mainVoltageLimitsSettingsModel: MainVoltageLimitsSettingsModel
}

const MainVoltageLimitsSettings: React.FC<Props> = ({ mainVoltageLimitsSettingsModel }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights
  const defaultValues = useMemo(() => getDefaultValues(mainVoltageLimitsSettingsModel), [mainVoltageLimitsSettingsModel])
  const methods = useForm<MainVoltageLimitsSettingsForm>({
    resolver: zodResolver(getMainVoltageLimitsSettingsSchema(mainVoltageLimitsSettingsModel)),
    defaultValues,
    mode: 'onSubmit',
  })
  useSyncFormWithValues(methods.reset, defaultValues)

  const { isLoading, updateGridAndSystemProtection } = useGridAndSystemProtectionMutation()

  const onSubmit = useCallback(
    async (data: MainVoltageLimitsSettingsForm): Promise<void> => {
      try {
        await updateGridAndSystemProtection({ mainVoltageLimitsSettings: data }, FUNCTION_NUMBER.voltageMonitoringSettings)
        methods.reset(data)
      } catch (error) {
        methods.reset()
      }
    },
    [methods, updateGridAndSystemProtection]
  )

  return (
    <FormProvider {...methods}>
      <Grid item xs={12}>
        <AccordionItem title={t(`${NAME_SPACE}.title`)} accordionDetailsSx={{ padding: 0 }}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <SettingsFormContainer onReset={methods.reset} isLoading={isLoading}>
              <SettingsBox
                title={t(`${NAME_SPACE}.voltage-gain-protection.title`)}
                suffix={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <NumberField
                        name="voltageGainProtection.largerVoltage"
                        label={t(`${NAME_SPACE}.voltage-gain-protection.larger-voltage.label`)}
                        suffix={UnitTypes.volt}
                        disabled={!canEdit || isFieldDisabled(mainVoltageLimitsSettingsModel.voltageGainProtection.largerVoltage)}
                        sx={getChangedFieldStyle(mainVoltageLimitsSettingsModel.voltageGainProtection.largerVoltage.changed)}
                      />
                      <NumberField
                        name="voltageGainProtection.largerTimeSeconds"
                        label={t(`${NAME_SPACE}.voltage-gain-protection.larger-time.label`)}
                        suffix={UnitTypes.second}
                        disabled={!canEdit || isFieldDisabled(mainVoltageLimitsSettingsModel.voltageGainProtection.largerTimeSeconds)}
                        sx={getChangedFieldStyle(mainVoltageLimitsSettingsModel.voltageGainProtection.largerTimeSeconds.changed)}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <NumberField
                        name="voltageGainProtection.voltage"
                        label={t(`${NAME_SPACE}.voltage-gain-protection.voltage.label`)}
                        suffix={UnitTypes.volt}
                        disabled={!canEdit || isFieldDisabled(mainVoltageLimitsSettingsModel.voltageGainProtection.voltage)}
                        sx={getChangedFieldStyle(mainVoltageLimitsSettingsModel.voltageGainProtection.voltage.changed)}
                      />
                      <NumberField
                        name="voltageGainProtection.timeSeconds"
                        label={t(`${NAME_SPACE}.voltage-gain-protection.time.label`)}
                        suffix={UnitTypes.second}
                        disabled={!canEdit || isFieldDisabled(mainVoltageLimitsSettingsModel.voltageGainProtection.timeSeconds)}
                        sx={getChangedFieldStyle(mainVoltageLimitsSettingsModel.voltageGainProtection.timeSeconds.changed)}
                      />
                    </Box>
                  </Box>
                }
              />
              <SettingsBox
                title={t(`${NAME_SPACE}.voltage-drop-protection.title`)}
                suffix={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <NumberField
                        name="voltageDropProtection.voltage"
                        label={t(`${NAME_SPACE}.voltage-drop-protection.voltage.label`)}
                        suffix={UnitTypes.volt}
                        disabled={!canEdit || isFieldDisabled(mainVoltageLimitsSettingsModel.voltageDropProtection.voltage)}
                        sx={getChangedFieldStyle(mainVoltageLimitsSettingsModel.voltageDropProtection.voltage.changed)}
                      />
                      <NumberField
                        name="voltageDropProtection.timeSeconds"
                        label={t(`${NAME_SPACE}.voltage-drop-protection.time.label`)}
                        suffix={UnitTypes.second}
                        disabled={!canEdit || isFieldDisabled(mainVoltageLimitsSettingsModel.voltageDropProtection.timeSeconds)}
                        sx={getChangedFieldStyle(mainVoltageLimitsSettingsModel.voltageDropProtection.timeSeconds.changed)}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <NumberField
                        name="voltageDropProtection.largerVoltage"
                        label={t(`${NAME_SPACE}.voltage-drop-protection.larger-voltage.label`)}
                        suffix={UnitTypes.volt}
                        disabled={!canEdit || isFieldDisabled(mainVoltageLimitsSettingsModel.voltageDropProtection.largerVoltage)}
                        sx={getChangedFieldStyle(mainVoltageLimitsSettingsModel.voltageDropProtection.largerVoltage.changed)}
                      />
                      <NumberField
                        name="voltageDropProtection.largerTimeSeconds"
                        label={t(`${NAME_SPACE}.voltage-drop-protection.larger-time.label`)}
                        suffix={UnitTypes.second}
                        disabled={!canEdit || isFieldDisabled(mainVoltageLimitsSettingsModel.voltageDropProtection.largerTimeSeconds)}
                        sx={getChangedFieldStyle(mainVoltageLimitsSettingsModel.voltageDropProtection.largerTimeSeconds.changed)}
                      />
                    </Box>
                  </Box>
                }
              />
              <Typography variant="caption">{`${NAME_SPACE}.bottom-text`}</Typography>
            </SettingsFormContainer>
          </Box>
        </AccordionItem>
      </Grid>
    </FormProvider>
  )
}

export default MainVoltageLimitsSettings

const getDefaultValues = (data: MainVoltageLimitsSettingsModel): MainVoltageLimitsSettingsForm => {
  return {
    voltageGainProtection: {
      voltage: data.voltageGainProtection.voltage.value,
      timeSeconds: data.voltageGainProtection.timeSeconds.value,
      largerVoltage: data.voltageGainProtection.largerVoltage.value,
      largerTimeSeconds: data.voltageGainProtection.largerTimeSeconds.value,
    },
    voltageDropProtection: {
      voltage: data.voltageDropProtection.voltage.value,
      timeSeconds: data.voltageDropProtection.timeSeconds.value,
      largerVoltage: data.voltageDropProtection.largerVoltage.value,
      largerTimeSeconds: data.voltageDropProtection.largerTimeSeconds.value,
    },
  }
}
